"use client";

import { useState } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";

interface Props {
  channelId: string;
  onInviteSent: () => void;
}

export function InviteForm({ channelId, onInviteSent }: Props) {
  const { token } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !name.trim() || !phone.trim()) return;

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await apiFetch<{ directlyAdded: boolean }>(
        `/channels/${channelId}/invites`,
        token,
        {
          method: "POST",
          body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
          suppressToast: true,
        }
      );

      if (result.directlyAdded) {
        setSuccess(`${name.trim()} has been added to the channel`);
      } else {
        setSuccess(`Invite created for ${name.trim()}`);
      }

      setName("");
      setPhone("");
      onInviteSent();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input
        type="text"
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={inputStyle}
        autoComplete="off"
      />
      <input
        type="tel"
        placeholder="Phone number"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        style={inputStyle}
        autoComplete="off"
      />
      <button
        type="submit"
        disabled={submitting || !name.trim() || !phone.trim()}
        style={{
          ...buttonStyle,
          opacity: submitting || !name.trim() || !phone.trim() ? 0.5 : 1,
        }}
      >
        {submitting ? "Sending..." : "Send Invite"}
      </button>
      {error && (
        <div style={{ fontSize: "0.75rem", color: "var(--color-danger)" }}>{error}</div>
      )}
      {success && (
        <div style={{ fontSize: "0.75rem", color: "#22c55e" }}>{success}</div>
      )}
    </form>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--color-bg-input)",
  border: "1px solid var(--color-border-secondary)",
  borderRadius: 6,
  padding: "0.5rem 0.75rem",
  fontSize: "0.85rem",
  color: "var(--color-text)",
  outline: "none",
};

const buttonStyle: React.CSSProperties = {
  background: "var(--color-border)",
  border: "1px solid var(--color-border-light)",
  borderRadius: 6,
  padding: "0.5rem 0.75rem",
  fontSize: "0.85rem",
  color: "var(--color-text)",
  cursor: "pointer",
};
